'use client';

import React, { useRef, useEffect, useState } from 'react';
import { perspectiveFaces } from '@/data/perspectiveData';
import { ChevronUp, ChevronDown } from 'lucide-react';
import gsap from 'gsap';

import { useLanguage } from '@/context/LanguageContext';
import { TranslationKey } from '@/translations/translations';

interface PerspectiveDialProps {
    activeFaceId: string;
    onPerspectiveChange: (faceId: string) => void;
    downArrowRef?: React.RefObject<HTMLButtonElement>;
}

const PerspectiveDial: React.FC<PerspectiveDialProps> = ({ activeFaceId, onPerspectiveChange, downArrowRef }) => {
    const { t } = useLanguage();
    const [isAnimating, setIsAnimating] = useState(false);
    const [prevFaceId, setPrevFaceId] = useState(activeFaceId);
    const [direction, setDirection] = useState<'up' | 'down'>('up');
    const [showIndicator, setShowIndicator] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const indicatorRef = useRef<HTMLDivElement>(null);

    const currentIndex = perspectiveFaces.findIndex(face => face.id === activeFaceId);

    // Flash indicator animation after 3 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            if (indicatorRef.current) {
                // Flash 3 times then fade out
                gsap.fromTo(indicatorRef.current,
                    { opacity: 0, scale: 0.8 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.5,
                        repeat: 5, // 3 flashes (on-off-on-off-on-off)
                        yoyo: true,
                        ease: 'power2.inOut',
                        onComplete: () => {
                            // Fade out and hide after flashing
                            gsap.to(indicatorRef.current, {
                                opacity: 0,
                                duration: 0.5,
                                onComplete: () => setShowIndicator(false)
                            });
                        }
                    }
                );
            }
        }, 3000); // Wait 3 seconds before starting the flash animation

        return () => clearTimeout(timer);
    }, []);

    // Hide indicator when user interacts
    const handlePreviousWithHideIndicator = () => {
        setShowIndicator(false);
        handlePrevious();
    };

    const handleNextWithHideIndicator = () => {
        setShowIndicator(false);
        handleNext();
    };

    // Get the face at a given offset from current
    const getFaceAtOffset = (offset: number) => {
        const index = (currentIndex + offset + perspectiveFaces.length) % perspectiveFaces.length;
        return perspectiveFaces[index];
    };

    const handlePrevious = () => {
        if (isAnimating) return;
        setDirection('down');
        setPrevFaceId(activeFaceId);
        setIsAnimating(true);

        const prevFace = getFaceAtOffset(-1);
        onPerspectiveChange(prevFace.id);
    };

    const handleNext = () => {
        if (isAnimating) return;
        setDirection('up');
        setPrevFaceId(activeFaceId);
        setIsAnimating(true);

        const nextFace = getFaceAtOffset(1);
        onPerspectiveChange(nextFace.id);
    };

    // GSAP Animation
    useEffect(() => {
        if (isAnimating && containerRef.current) {
            // Kill any running animations
            gsap.killTweensOf(containerRef.current);

            if (direction === 'up') {
                // Moving Next: Slide up (0% -> -50%)
                gsap.fromTo(containerRef.current,
                    { y: '0%' },
                    {
                        y: '-50%',
                        duration: 0.4,
                        ease: 'power2.inOut',
                        onComplete: () => setIsAnimating(false)
                    }
                );
            } else {
                // Moving Prev: Slide down (-50% -> 0%)
                gsap.fromTo(containerRef.current,
                    { y: '-50%' },
                    {
                        y: '0%',
                        duration: 0.4,
                        ease: 'power2.inOut',
                        onComplete: () => setIsAnimating(false)
                    }
                );
            }
        }
    }, [isAnimating, direction]);

    const currentFace = perspectiveFaces.find(f => f.id === activeFaceId) || perspectiveFaces[0];
    const prevFace = perspectiveFaces.find(f => f.id === prevFaceId) || perspectiveFaces[0];

    // Helper to get translated name
    const getFaceName = (faceId: string) => {
        return t(`about.perspective.${faceId}.name` as TranslationKey);
    };

    // Text style shared between static and animated parts
    const textStyle = "text-5xl md:text-7xl font-bold tracking-tight leading-none text-white block text-center md:text-left";
    const shadowStyle = {
        textShadow: '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)'
    };

    return (
        <div className="w-full flex flex-col md:grid md:grid-cols-2 items-center">
            {/* Static "Developing" text (Translated Prefix) */}
            <div className="flex justify-center md:justify-end md:pr-3 lg:pr-4">
                <span
                    className={textStyle}
                    style={shadowStyle}
                >
                    {t('about.perspective.prefix')}
                </span>
            </div>

            {/* Animated Column for Perspective Name - Centered on mobile, left aligned on desktop */}
            <div className="flex flex-col items-center md:items-start justify-center md:pl-3 lg:pl-4 mt-2 md:mt-0">
                {/* Up arrow button */}
                <button
                    onClick={handlePreviousWithHideIndicator}
                    className="md:ml-8 lg:ml-16 p-1 rounded-full hover:bg-white/10 transition-all duration-300 group z-10 pointer-events-auto"
                    aria-label="Previous perspective"
                >
                    <ChevronUp
                        size={24}
                        className="text-white/50 group-hover:text-white transition-colors"
                        style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' }}
                    />
                </button>

                {/* Animated Word Container */}
                <div className="h-[1.3em] text-5xl md:text-7xl overflow-hidden relative min-w-[350px] md:min-w-[500px] flex items-center justify-center md:justify-start">
                    <div
                        ref={containerRef}
                        className="flex flex-col items-center md:items-start absolute w-full"
                        style={{ transform: isAnimating ? undefined : 'none' }}
                        aria-hidden="true"
                    >
                        {isAnimating ? (
                            direction === 'up' ? (
                                // Moving UP: [Prev, Current]
                                <>
                                    <span className={textStyle} style={{ ...shadowStyle, color: prevFace.color }}>
                                        {getFaceName(prevFace.id)}
                                    </span>
                                    <span className={textStyle} style={{ ...shadowStyle, color: currentFace.color }}>
                                        {getFaceName(currentFace.id)}
                                    </span>
                                </>
                            ) : (
                                // Moving DOWN: [Current, Prev]
                                <>
                                    <span className={textStyle} style={{ ...shadowStyle, color: currentFace.color }}>
                                        {getFaceName(currentFace.id)}
                                    </span>
                                    <span className={textStyle} style={{ ...shadowStyle, color: prevFace.color }}>
                                        {getFaceName(prevFace.id)}
                                    </span>
                                </>
                            )
                        ) : (
                            // Static state
                            <span className={textStyle} style={{ ...shadowStyle, color: currentFace.color }}>
                                {getFaceName(currentFace.id)}
                            </span>
                        )}
                    </div>
                    <span className="sr-only" aria-live="polite" aria-atomic="true">
                        {getFaceName(currentFace.id)}
                    </span>
                </div>

                {/* Down arrow button with flashing indicator */}
                <div className="relative z-[100] md:ml-8 lg:ml-16">
                    <button
                        ref={downArrowRef}
                        onClick={handleNextWithHideIndicator}
                        className="p-1 rounded-full hover:bg-white/10 transition-all duration-300 group relative z-[101] pointer-events-auto"
                        aria-label="Next perspective"
                    >
                        <ChevronDown
                            size={24}
                            className="text-white/50 group-hover:text-white transition-colors"
                            style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' }}
                        />
                    </button>

                    {/* Flashing white indicator */}
                    {showIndicator && (
                        <div
                            ref={indicatorRef}
                            className="absolute inset-0 pointer-events-none flex items-center justify-center z-[102]"
                            style={{ opacity: 0 }}
                        >
                            <div
                                className="w-14 h-14 rounded-full border-4 border-white"
                                style={{
                                    boxShadow: '0 0 40px rgba(255,255,255,1), 0 0 80px rgba(255,255,255,0.8), inset 0 0 40px rgba(255,255,255,0.6)'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PerspectiveDial;
